/**
 * Macaw (ex-Alisa) images are uploaded to S3 by the backend. The frontend gets them from
 * `GET /transactions/trucks/{id}/images` and `TransactionImageStatusMessage` as
 * `{ id, version, image_category, camera_name, status, url, asset_id }`, where `url` is a presigned S3 link
 * set only while the image is READY and `asset_id` lets the agent delete the image.
 */
export const MACAW_IMAGE_STATUS = Object.freeze({
    UPLOADING: 'UPLOADING',
    READY: 'READY',
    FAILED: 'FAILED',
});

/**
 * Image source for an `<img>`: the S3 link of a Macaw image, or a data URL for images that exist
 * only in the browser (agent camera snapshots). `null` while a Macaw image is not uploaded yet.
 */
export const getImageSrc = (image) => {
    if (image?.url) return image.url;
    // image/jpg as before, it gives downloaded snapshots their .jpg extension
    if (image?.encodedImage) return `data:image/jpg;base64,${image.encodedImage}`;
    return null;
};

/**
 * Merges Macaw images from the API or the websocket into the current list, by id, in place
 * (called from an Immer reducer). Rules:
 * - a READY image never goes back to UPLOADING / FAILED (late or stale message);
 * - an image that is already READY keeps its link, because every response signs a new one and a
 *   different link would make the browser download the image again; `refreshUrls` replaces it
 *   (used when the current link expired);
 * - new images get `imageName`, the list stays ordered by id.
 */
export const mergeMacawImages = (images, incomingImages, { imageName, refreshUrls = false }) => {
    const byId = new Map(images.map((image) => [image.id, image]));

    incomingImages.forEach((incoming) => {
        const { id, version, image_category: imageCategory, camera_name: cameraName = null, status, url = null, asset_id: assetId = null } = incoming;
        const existing = byId.get(id);

        if (!existing) {
            const image = { id, version, imageCategory, cameraName, imageName, timestamp: null, status, url, assetId };
            images.push(image);
            byId.set(id, image);
            return;
        }

        if (existing.status === MACAW_IMAGE_STATUS.READY && status !== MACAW_IMAGE_STATUS.READY) return;

        existing.version = version;
        existing.status = status;
        existing.assetId = assetId ?? existing.assetId;
        if (status !== MACAW_IMAGE_STATUS.READY) {
            existing.url = null;
        } else if (!existing.url || refreshUrls) {
            existing.url = url;
        }
    });

    images.sort((first, second) => first.id - second.id);
    return images;
};

/**
 * Upload progress of the Macaw images of a transaction (snapshots are not counted).
 *
 * @returns {{ ready: number, total: number, isLoading: boolean }} `isLoading` while at least one image is still uploading
 */
export const getMacawImagesProgress = (images = []) => {
    let ready = 0;
    let total = 0;
    let uploading = 0;
    images.forEach((image) => {
        if (!image?.status) return;
        total++;
        if (image.status === MACAW_IMAGE_STATUS.READY) ready++;
        if (image.status === MACAW_IMAGE_STATUS.UPLOADING) uploading++;
    });
    return { ready, total, isLoading: uploading > 0 };
};
